// NotificationAdapter — Default implementation for user notifications
// Injected into VNInvestCharts to avoid direct window.alert/confirm/prompt access
// All global access is lazy-initialized inside the factory function for tree-shaking
// ============================================
// Default Notification Adapter (uses window.alert/confirm/prompt)
// ============================================
export const createDefaultNotificationAdapter = () => {
    return {
        alert: (message) => {
            if (typeof window !== 'undefined' && window.alert) {
                window.alert(message);
            }
        },
        confirm: (message) => {
            if (typeof window !== 'undefined' && window.confirm) {
                return window.confirm(message);
            }
            return false;
        },
        prompt: (message, defaultValue) => {
            if (typeof window !== 'undefined' && window.prompt) {
                return window.prompt(message, defaultValue);
            }
            return null;
        },
    };
};
// Singleton - lazy initialized on first access
let _defaultNotificationAdapter = null;
export const getDefaultNotificationAdapter = () => {
    if (!_defaultNotificationAdapter) {
        _defaultNotificationAdapter = createDefaultNotificationAdapter();
    }
    return _defaultNotificationAdapter;
};
// Backward compatibility - lazy getter
export const defaultNotificationAdapter = new Proxy({}, {
    get(target, prop) {
        const adapter = getDefaultNotificationAdapter();
        return adapter[prop];
    }
});
export const createMockNotificationAdapter = () => {
    const adapter = {
        alert: (message) => {
            adapter.alertCalls.push(message);
        },
        confirm: (message) => {
            adapter.confirmCalls.push(message);
            return adapter.confirmResult;
        },
        prompt: (message, defaultValue) => {
            adapter.promptCalls.push(message);
            return adapter.promptResult;
        },
        alertCalls: [],
        confirmCalls: [],
        promptCalls: [],
        confirmResult: true,
        promptResult: null,
        resetCalls: () => {
            adapter.alertCalls = [];
            adapter.confirmCalls = [];
            adapter.promptCalls = [];
        },
        setConfirmResult: (result) => { adapter.confirmResult = result; },
        setPromptResult: (result) => { adapter.promptResult = result; },
    };
    return adapter;
};
// ============================================
// No-op Notification Adapter (for SSR/headless)
// ============================================
export const createNoopNotificationAdapter = () => ({
    alert: () => { },
    confirm: () => false,
    prompt: () => null,
});
